import React from "react";
import { useTranslation } from "react-i18next";
import { useUpdateCartItemQuantity } from "../../hooks/useUpdateCartItemQuantity";
import { useRemoveCartItemFromCart } from "../../hooks/useRemoveCartItemFromCart";
import { useMoveToSavedForLaterFromCart } from "../../../savedForLater/hooks/useMoveToSavedForLaterFromCart";
import { useMoveCartItemToWishlist } from "../../hooks/useMoveCartItemToWishlist";
import { useToggleCompareItem } from "../../../compare/hooks/useToggleCompareItem";
import type { TransformedCartItem } from "../../types/cartApiTypes";
import QuantityControl from "../common/QuantityControl";
import CartItemActions from "../common/CartItemActions";
import { decodeHtmlEntities } from "../../../../../common/helpers/decodeHtmlEntities";

interface CartPageItemProps {
  item: TransformedCartItem;
}

/**
 * Renders a single cart item using a div-based grid layout.
 */
const CartPageItem: React.FC<CartPageItemProps> = ({ item }) => {
  const { t } = useTranslation();

  // --- Logic: If sold individually, force max quantity limit to 1 ---
  const minQuantity = item.quantityLimits.minimum;
  const maxQuantity = item.soldIndividually ? 1 : item.quantityLimits.maximum;

  // --- Hooks ---
  const { quantity, handleQuantityChange, isUpdating } =
    useUpdateCartItemQuantity(item.key, item.quantity);

  const { handleRemoveCartItem: triggerRemove, isRemoving } =
    useRemoveCartItemFromCart();

  const { handleMoveCartItemToWishlist, isMoving } =
    useMoveCartItemToWishlist();

  const { handleMoveToSavedForLater, isMoving: isSaving } =
    useMoveToSavedForLaterFromCart();

  // Use item.id directly. For variations, this is the Variation ID.
  const { handleToggle: handleCompareToggle, isInCompare } =
    useToggleCompareItem(item.id);

  // --- Event Handlers ---
  const onQuantityInputChange = (newQuantity: number) => {
    handleQuantityChange(newQuantity);
  };

  const onRemoveClick = () => {
    triggerRemove({ key: item.key });
  };

  const onMoveToWishlistClick = () => {
    handleMoveCartItemToWishlist(item.key);
  };

  // Saved For Later: Now uses atomic move
  const onSaveForLaterClick = () => {
    handleMoveToSavedForLater(item.key);
  };

  // --- Combined loading state ---
  const isLoading = isUpdating || isRemoving || isMoving || isSaving;

  const image = item.images.length > 0 ? item.images[0] : null;

  // Helper to ensure clean text for display
  const decodedName = decodeHtmlEntities(item.name);

  // --- BUY X PAY Y PROMOTION LOGIC ---
  // If the backend has set the *active* transaction price to 0, it's a gift.
  const isGift = item.rawPrice === "0";
  const promo = item.buyXPayYPromotion;

  return (
    <div className="cart-page-item-wrapper">
      {/* Primary Actions (Wishlist & Remove) */}
      <CartItemActions
        onMoveToWishlist={onMoveToWishlistClick}
        onRemove={onRemoveClick}
        isMoving={isMoving}
        isRemoving={isRemoving}
        itemName={decodedName}
        context="page"
        placement="top"
      />

      {/* Main content row for the item */}
      <div className="cart-page-item">
        <div
          className="cart-page-item__cell cart-page-item__product"
          data-label={t("cartPage.cartPageList.product", "Product")}
        >
          <a href={item.permalink} className="cart-page-item__image-link">
            {image ? (
              <img src={image.thumbnail} alt={image.alt || decodedName} />
            ) : (
              <div className="placeholder-image"></div>
            )}
          </a>
          <div className="cart-page-item__details">
            <a href={item.permalink} className="cart-page-item__name">
              {decodedName}
            </a>

            {/* --- DYNAMIC STOCK BADGES & STATUSES --- */}
            <div
              className="cart-item-stock-status-wrapper"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                marginTop: "4px",
              }}
            >
              {item.soldIndividually && (
                <span
                  style={{
                    fontSize: "11px",
                    color: "var(--color-text-secondary)",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  {t("cartItem.soldIndividually", "Sold Individually")}
                </span>
              )}
              {item.showBackorderBadge && (
                <span
                  style={{
                    fontSize: "11px",
                    color: "var(--color-info)",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  {t("cartItem.backorder", "On Backorder")}
                </span>
              )}
              {!item.soldIndividually && item.lowStockRemaining !== null && (
                <span
                  style={{
                    fontSize: "11px",
                    color: "var(--color-danger)",
                    fontWeight: "bold",
                  }}
                >
                  {t("productCard.lowStockBadge", {
                    count: item.lowStockRemaining,
                  })}
                </span>
              )}
            </div>

            {promo && (
              <span className="cart-item-promo-badge">
                {t("productCard.buyXPayYBadge", {
                  buy: promo.buyQty,
                  pay: promo.payQty,
                  defaultValue: `Buy ${promo.buyQty} Pay ${promo.payQty}!`,
                })}
              </span>
            )}

            {item.variation.length > 0 && (
              <div className="cart-page-item__meta">
                {item.variation.map((v) => (
                  <div key={v.attribute}>
                    <strong>{v.attribute}:</strong>{" "}
                    {decodeHtmlEntities(v.value)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div
          className="cart-page-item__cell cart-page-item__price"
          data-label={t("cartPage.cartPageList.price", "Price")}
        >
          {isGift ? (
            <span className="is-gift">{t("feedback.cart.gift", "Gift!")}</span>
          ) : (
            // Display the Catalog Price (Original Unit Price)
            item.catalogPrice
          )}
        </div>
        <div
          className="cart-page-item__cell cart-page-item__quantity"
          data-label={t("cartPage.cartPageList.quantity", "Quantity")}
        >
          <QuantityControl
            value={quantity}
            onChange={onQuantityInputChange}
            min={minQuantity}
            max={maxQuantity}
            // Disable controller if loading OR if item is sold individually (always locked to 1)
            disabled={isLoading || item.soldIndividually}
            ariaLabel={`Quantity for ${decodedName}`}
            className="cart-page-item__quantity-control"
          />
        </div>
        <div
          className="cart-page-item__cell cart-page-item__line-total"
          data-label={t("cartPage.cartPageList.lineTotal", "Line Total")}
        >
          {isGift ? (
            <span className="is-gift">{t("feedback.cart.gift", "Gift!")}</span>
          ) : (
            item.formattedCalculatedLineTotal
          )}
        </div>
      </div>

      {/* Secondary Actions (Save for Later & Compare) */}
      <CartItemActions
        onSaveForLater={onSaveForLaterClick}
        onToggleCompare={handleCompareToggle}
        isInCompare={isInCompare}
        isSaving={isSaving}
        itemName={decodedName}
        context="page"
        placement="bottom"
      />
    </div>
  );
};

export default CartPageItem;
